import { XUIEditableTableCellAutocompleterSecondarySearch } from '@xero/xui/react/editabletable';
import XUIPicklist, { XUIPickitem } from '@xero/xui/react/picklist';
import Fuse from 'fuse.js';
import { Dispatch, FC, SetStateAction, useMemo, useState } from 'react';
import { useSelector } from 'react-redux';
import { IPaymentGateway, IPaymentGatewayDetail, ITablePaymentGatewayDetail } from '../../../../../models';
import { IShopifySettingsResponse } from '../../../../../models/shopify';
import { settingsSelectors } from '../../../../../redux/slices/SettingsSlice';
import { deepClone } from '../../../../../redux/store';
import { IDataRow } from './models';

export interface IGatewayDropdownProps {
    tableDataState: [ITablePaymentGatewayDetail[], Dispatch<SetStateAction<ITablePaymentGatewayDetail[]>>];
    deletedGateways: string[];
    onGatewayChange: (paymentGatewayDetails: Record<string, IPaymentGatewayDetail>, paymentGatewayCode?: string) => void;
    dataRow: IDataRow;
    lang: Record<string, any>;
    setIsAddGatewayButtonDisabled: React.Dispatch<React.SetStateAction<boolean>>;
    setSelectPGCellInvalid: React.Dispatch<React.SetStateAction<boolean>>;
}

export const GatewayDropdown: FC<IGatewayDropdownProps> = ({
    tableDataState,
    deletedGateways,
    onGatewayChange,
    dataRow,
    lang,
    setIsAddGatewayButtonDisabled,
    setSelectPGCellInvalid,
}) => {
    const settings = useSelector(settingsSelectors.settings) as IShopifySettingsResponse;
    const paymentGateways = settings?.paymentGateways.filter((g) => g.isPopular);

    const [dropDownData, setDropDownData] = useState(paymentGateways);
    const [currentTableData, setCurrentTableData] = tableDataState;

    const handleGatewaySelection = (gateway: IPaymentGateway) => () => {
        const paymentGatewayDetails = deepClone(settings.paymentGatewayDetails);
        if (paymentGatewayDetails) {
            const index = currentTableData.findIndex(
                (item: { paymentType: string }) => item.paymentType === dataRow.paymentType
            );
            currentTableData[index] = {
                ...currentTableData[index],
                paymentType: gateway.code,
                createInvoice: true,
            };
            setCurrentTableData(currentTableData);

            paymentGatewayDetails[gateway.code] = {
                ...paymentGatewayDetails[dataRow.paymentType],
                createInvoice: true,
            };

            delete paymentGatewayDetails[dataRow.paymentType];

            onGatewayChange(paymentGatewayDetails, gateway.code);
            if (dataRow.paymentType === lang.ROW_SELECT_PAYMENT_TYPE) {
                setSelectPGCellInvalid(false);
            }
        }
    };

    const fuse = useMemo(
        () =>
            paymentGateways
                ? new Fuse(paymentGateways, {
                      keys: ['name'],
                  })
                : null,
        [paymentGateways]
    );

    const onSearch = (value: string) => {
        const fuzzySearchResult = fuse?.search(value).map((m) => m.item);

        fuzzySearchResult && fuzzySearchResult.length > 0
            ? setDropDownData(fuzzySearchResult)
            : setDropDownData(paymentGateways);
    };

    const onClose = () => {
        setDropDownData(paymentGateways);
    };

    const isSelectedPaymentGateway = (paymentGateway: IPaymentGateway | undefined, code: string): boolean => {
        return code === paymentGateway?.code;
    };

    const isDuplicatePaymentGatewayName = (name: string): boolean => {
        return (
            !!paymentGateways?.filter((pg) => pg.name === name) &&
            paymentGateways?.filter((pg) => pg.name === name).length > 1
        );
    };

    const getButtonContent = (dr: IDataRow) => {
        const selected = paymentGateways?.find((f) => f.code === dr.paymentType);
        const buttonContent = selected?.name ?? dr.paymentType;
        return isDuplicatePaymentGatewayName(buttonContent) ? (
            <>
                <span className='gateway-item'>{buttonContent}</span>
                <span className='gateway-code'>({selected?.code})</span>{' '}
            </>
        ) : (
            <span className='gateway-item'>{buttonContent}</span>
        );
    };

    const onOptionSelect = () => {
        let allGatewaysSelected = true;
        paymentGateways?.forEach((pg) => {
            if (!currentTableData.some((ctd) => ctd.paymentType === pg.code)) {
                allGatewaysSelected = false;
            }
        });
        setIsAddGatewayButtonDisabled(allGatewaysSelected);
    };

    const isPaymentGatewayDisabled = (gateway: IPaymentGateway) => {

        const isSelectedPG = isSelectedPaymentGateway(
            paymentGateways?.find((f) => f.code === dataRow.paymentType),
            gateway.code,
        );
        const isDeletedPG = deletedGateways.includes(gateway.code);

        if (isDeletedPG) {
            //if a gateway has been deleted by a user, don't disable it in the drop-down
            return false;
        } else {
            return isSelectedPG ? false : Object.prototype.hasOwnProperty.call(settings.paymentGatewayDetails, gateway.code);
        }
    };

    return (
        <XUIEditableTableCellAutocompleterSecondarySearch
            buttonContent={getButtonContent(dataRow)}
            onSearch={onSearch}
            onClose={onClose}
            footer={<div className='xui-padding gateway-help'>{lang.PAYMENT_GATEWAY_HELP.PREAMBLE}</div>}
            matchTriggerWidth={true}
            onOptionSelect={onOptionSelect}
            isInvalid={
                dataRow.paymentType === lang.ROW_SELECT_PAYMENT_TYPE &&
                (!!dataRow.bankAccount || !!dataRow.clearingAccount)
            }
            validationMessage={lang.TABLE_PG_CELL_VALIDATION_MESSAGE}
            placeholder='Search'
            inputLabel={'[Placeholder] Search'}
        >
            <XUIPicklist>
                <div className='xui-padding gateway-help xui-pickitem--secondary'>
                    {lang.PAYMENT_GATEWAY_HELP.POPULAR}
                </div>
                {dropDownData?.map((m, i) => (
                    <XUIPickitem
                        id={`payment-gateway-dropdown-${i}`}
                        key={i}
                        onClick={handleGatewaySelection(m)}
                        isSelected={isSelectedPaymentGateway(
                            paymentGateways?.find((f) => f.code === dataRow.paymentType),
                            m.code
                        )}
                        isDisabled={isPaymentGatewayDisabled(m)}
                    >
                        {m.name}
                        {isDuplicatePaymentGatewayName(m.name) ? (
                            <span className='gateway-code xui-margin-left-2xsmall'>({m.code})</span>
                        ) : null}
                    </XUIPickitem>
                ))}
            </XUIPicklist>
        </XUIEditableTableCellAutocompleterSecondarySearch>
    );
};
